//
// Copyright 2022 DXOS.org
//

import { Event, type ReadOnlyEvent, asyncTimeout } from '@dxos/async';
import { Context } from '@dxos/context';
import { Obj, Query, type QueryResult } from '@dxos/echo';
import { filterMatchDoc } from '@dxos/echo-host/filter';
import { GroupBy, QueryPlanner, queryContainsChanges } from '@dxos/echo-host/query';
import { type EntityStructure, QueryAST } from '@dxos/echo-protocol';
import { type EntityId, type SpaceId } from '@dxos/keys';
import { log } from '@dxos/log';

import { type ItemsUpdatedEvent, type ObjectCore } from '../core-db/index.ts';
import { type DatabaseImpl } from '../proxy-db/index.ts';
import {
  type MergePlan,
  compareWindowData,
  createMergePlan,
  evaluateCore,
  keepRemoteEntry,
  windowData,
  writeAffects,
} from './merge-plan.ts';
import { type QueryContext, type SourceEntry } from './query-context.ts';
import {
  getTargetSpacesForQuery,
  isSimpleSelectionQuery,
  queryAggregateNeedsIndex,
  queryHasWindowing,
  queryTargetsSpacesOrFeeds,
} from './util.ts';
import { type WorkingSetDataProvider, type WorkingSetItem, WorkingSetQueryExecutor } from './working-set-executor.ts';

export type GraphQueryContextProps = {
  // TODO(dmaretskyi): Make async.
  onStart: () => void;
  onStop: () => void;
};

/**
 * Query data source.
 * Implemented by a space or a remote agent.
 * Each query has a separate instance.
 */
export interface QuerySource {
  // TODO(dmaretskyi): Update info?
  changed: Event<void>;

  /** Where entries come from. `local` and `registry` are in-process; anything else is remote evidence. */
  readonly kind: QueryResult.Source;

  /** Whether this source serves the current query at all. */
  isServing(): boolean;

  /** Answers integrated for the current query, re-runs that left it unchanged included; 0 when in-process. */
  answers(): number;

  /** Whether the current query's remote stream failed. */
  isFailed(): boolean;

  // TODO(dmaretskyi): Make async.
  open(): void;

  // TODO(dmaretskyi): Make async.
  close(): void;

  /**
   * Synchronous query.
   */
  getResults(): SourceEntry[];

  /**
   * Whether this source produces authoritative synchronous results for its current query (via
   * {@link getResults}). Sources that resolve asynchronously (e.g. index- or network-backed) return
   * false so callers can defer the initial subscription event until real results arrive.
   */
  isSynchronous(): boolean;

  /**
   * Whether this source serves the current query and has yet to answer it. An asynchronous source
   * stops pending once its first answer has been integrated, or once it fails.
   */
  isPending(): boolean;

  /**
   * One-shot query.
   */
  run(ctx: Context, query: QueryAST.Query): Promise<SourceEntry[]>;

  /**
   * Set the filter and trigger continuous updates.
   */
  update(query: QueryAST.Query): void;
}

/**
 * The local query engine: answers from the in-process sources and merges remote answers as evidence, which
 * local state revalidates before it is presented. See `docs/SYNC_QUERIES.md`.
 */
export class GraphQueryContext implements QueryContext {
  private readonly _sources = new Set<QuerySource>();

  private _query?: QueryAST.Query = undefined;

  private _ctx?: Context = undefined;

  #plan?: MergePlan = undefined;

  /** Remote answers integrated when a local write last fell in scope; the answer is stale until they pass it. */
  #staleAt?: number = undefined;

  public changed = new Event<void>();

  constructor(private readonly _params: GraphQueryContextProps) {}

  get sources(): ReadonlySet<QuerySource> {
    return this._sources;
  }

  start() {
    this._ctx = new Context();
    this._params.onStart();
    for (const source of this._sources) {
      source.open();
      if (this._query) {
        source.update(this._query);
      }

      // Subscribing after `update` means that we will intentionally skip any `changed` events generated by update.
      source.changed.on(this._ctx, () => {
        this.changed.emit();
      });
      this.#watchLocalCommits(source);
    }
  }

  stop() {
    void this._ctx?.dispose();
    this._ctx = undefined;
    for (const source of this.sources) {
      source.close();
    }
    this.#staleAt = undefined;
    this._params.onStop();
  }

  getResults(): SourceEntry[] {
    // TODO: dedup by meta.key based on scope order (space results take precedence over registry).
    if (!this._query || !this.#plan) {
      return [];
    }
    const local: SourceEntry[] = [];
    const remote: SourceEntry[] = [];
    for (const source of this._sources) {
      (isInProcess(source) ? local : remote).push(...source.getResults());
    }
    const remoteAnswered = this.#servingRemotes().some((source) => source.answers() > 0);
    return this.#merge(this._query, this.#plan, local, remote, remoteAnswered);
  }

  /**
   * Whether any source produces authoritative synchronous results for the current query. False when
   * the query is served only by asynchronous sources (e.g. a feed query served by the index).
   */
  isSynchronous(): boolean {
    if (!this._query) {
      return false;
    }
    return Array.from(this._sources).some((source) => source.isSynchronous());
  }

  hasPendingSources(): boolean {
    if (!this._query) {
      return true;
    }
    return Array.from(this._sources).some((source) => source.isPending());
  }

  getCompleteness(): QueryResult.Completeness {
    const plan = this.#plan;
    if (!this._query || !plan) {
      return 'pending';
    }
    const remote = this.#servingRemotes();
    if (remote.length === 0 || this.#holdsAllIds(plan)) {
      return 'complete';
    }
    if (remote.some((source) => source.isFailed())) {
      return 'failed';
    }
    const answered = remote.filter((source) => source.answers() > 0);
    if (answered.length === 0) {
      // The working set claims text and timestamp queries it then declines, so the AST decides, not the source.
      const localContributes =
        plan.characteristics.synchronous &&
        Array.from(this._sources).some((source) => isInProcess(source) && source.isServing());
      return localContributes ? 'local-only' : 'pending';
    }
    if (this.#staleAt !== undefined && this.#remoteAnswers() <= this.#staleAt) {
      return 'stale';
    }
    return answered.length < remote.length ? 'merged' : 'complete';
  }

  getSourceStates(): QueryResult.SourceState[] {
    return Array.from(this._sources)
      .filter((source) => source.isServing())
      .map((source) => ({
        source: source.kind,
        state: source.isFailed() ? 'failed' : isInProcess(source) || source.answers() > 0 ? 'answered' : 'pending',
        answers: source.answers(),
      }));
  }

  async run(
    ctx: Context,
    query: QueryAST.Query,
    { timeout = 30_000 }: QueryResult.RunOptions = {},
  ): Promise<SourceEntry[]> {
    const sources = [...this._sources];
    const ids = getSelectedIds(query);
    if (ids === undefined) {
      return this.#runSources(ctx, sources, query, timeout);
    }

    const deadline = performance.now() + timeout;
    const local = sources.filter((source) => source.isSynchronous());
    const results = await this.#runSources(ctx, local, query, timeout);
    const found = new Set(results.map((entry) => entry.id));
    const missing = ids.filter((id) => !found.has(id));
    if (missing.length === 0) {
      return results;
    }
    const remote = sources.filter((source) => !local.includes(source));
    const remaining = Math.max(0, deadline - performance.now());
    return [...results, ...(await this.#runSources(ctx, remote, selectIds(query, missing), remaining))];
  }

  async #runSources(
    ctx: Context,
    sources: QuerySource[],
    query: QueryAST.Query,
    timeout: number,
  ): Promise<SourceEntry[]> {
    const runTasks = sources.map(async (s) => {
      try {
        log('run query', {
          resolver: Object.getPrototypeOf(s).constructor.name,
          query: Query.pretty(Query.fromAst(query)),
        });
        const results = await asyncTimeout<SourceEntry[]>(s.run(ctx, query), timeout);
        log('run query results', {
          resolver: Object.getPrototypeOf(s).constructor.name,
          count: results.length,
          query: Query.pretty(Query.fromAst(query)),
        });
        return { inProcess: isInProcess(s), results };
      } catch (err) {
        log('run query error', {
          resolver: Object.getPrototypeOf(s).constructor.name,
          query: Query.pretty(Query.fromAst(query)),
          error: err,
        });
        throw err;
      }
    });
    if (runTasks.length === 0) {
      return [];
    }
    const answers = await Promise.all(runTasks);
    return this.#merge(
      query,
      createMergePlan(query),
      answers.flatMap((answer) => (answer.inProcess ? answer.results : [])),
      answers.flatMap((answer) => (answer.inProcess ? [] : answer.results)),
      answers.some((answer) => !answer.inProcess),
    );
  }

  update(query: QueryAST.Query): void {
    this._query = query;
    this.#plan = createMergePlan(query);
    this.#staleAt = undefined;
    for (const source of this._sources) {
      source.update(query);
    }
  }

  addQuerySource(querySource: QuerySource) {
    this._sources.add(querySource);
    if (this._ctx != null) {
      querySource.changed.on(this._ctx, () => {
        this.changed.emit();
      });
      this.#watchLocalCommits(querySource);
    }
    if (this._query) {
      querySource.update(this._query);
    }
  }

  /**
   * In-process entries, then every remote entry local evidence does not overrule, in natural order when the
   * query has no order of its own. A property-ordered window also admits local matches that sort within the
   * remote window, re-sorted and cut to its limit.
   */
  #merge(
    query: QueryAST.Query,
    plan: MergePlan,
    local: SourceEntry[],
    remote: SourceEntry[],
    remoteAnswered: boolean,
  ): SourceEntry[] {
    const kept = remote.filter((entry) => keepRemoteEntry(plan, entry));
    if (!plan.window) {
      const merged = [...local, ...kept];
      // Stable, so an in-process entry stays ahead of the remote duplicate that presentation drops.
      return plan.natural ? merged.sort((left, right) => compareIds(left.id, right.id)) : merged;
    }
    // A window is the remote's to define; before it answers there is nothing to admit into. An empty answer is an
    // answer, so local writes the index has not seen still enter it.
    if (!remoteAnswered) {
      return kept;
    }
    const { orders, limit } = plan.window;
    // Sort data is read once per candidate, since a comparison against the live document re-reads it on
    // every step of the sort, and only local matches that land inside the window are built into entries.
    const candidates: WindowCandidate[] = kept.map((entry) => ({ id: entry.id, data: windowData(entry), entry }));
    // A short remote window is the whole result, so every local match belongs; a full one bounds admission.
    const full = remote.length >= limit;
    const boundary = full ? windowData(remote[remote.length - 1]) : undefined;
    const admit = full ? (data: EntityStructure['data']) => compareWindowData(orders, data, boundary) <= 0 : undefined;
    const seen = new Set(kept.map((entry) => entry.id));
    for (const source of this._sources) {
      if (!(source instanceof SpaceQuerySource) || !source.coversQuery(query)) {
        continue;
      }
      for (const match of source.matchLocal(plan, admit)) {
        if (seen.has(match.id)) {
          continue;
        }
        seen.add(match.id);
        candidates.push({ id: match.id, data: match.data, source });
      }
    }
    // Stable, so entries the remote ordered as equals keep their order ahead of local matches that tie with them.
    candidates.sort((left, right) => compareWindowData(orders, left.data, right.data));
    const window: SourceEntry[] = [];
    for (const candidate of candidates) {
      if (window.length === limit) {
        break;
      }
      const entry = 'entry' in candidate ? candidate.entry : candidate.source.entryFor(candidate.id);
      if (entry) {
        window.push(entry);
      }
    }
    return window;
  }

  #watchLocalCommits(source: QuerySource): void {
    if (this._ctx == null || !(source instanceof SpaceQuerySource)) {
      return;
    }
    source.localCommits.on(this._ctx, (event) => this.#onLocalCommit(source, event));
  }

  /**
   * A local write in scope marks a remote answer that local evidence cannot fully overrule as stale, and
   * re-merges a query the working set does not serve itself (a window), whose entries it may now overrule.
   */
  #onLocalCommit(source: SpaceQuerySource, event: ItemsUpdatedEvent): void {
    const plan = this.#plan;
    if (!this._query || !plan || !source.coversQuery(this._query)) {
      return;
    }
    if (!event.itemsUpdated.some(({ id }) => writeAffects(plan, source.coreById(id)))) {
      return;
    }
    if (plan.staleOnLocalWrite && this.#servingRemotes().length > 0) {
      this.#staleAt = this.#remoteAnswers();
    }
    if (!source.isServing()) {
      this.changed.emit();
    }
  }

  #servingRemotes(): QuerySource[] {
    return Array.from(this._sources).filter((source) => !isInProcess(source) && source.isServing());
  }

  #remoteAnswers(): number {
    return this.#servingRemotes().reduce((sum, source) => sum + source.answers(), 0);
  }

  /** A by-id query is whole once every id it names is loaded with a body in a space it covers. */
  #holdsAllIds(plan: MergePlan): boolean {
    if (!plan.ids || plan.ids.length === 0 || !this._query) {
      return false;
    }
    const query = this._query;
    const spaces = Array.from(this._sources).filter(
      (source): source is SpaceQuerySource => source instanceof SpaceQuerySource && source.coversQuery(query),
    );
    return plan.ids.every((id) => spaces.some((source) => source.holdsBody(id)));
  }
}

/** A loaded object local evaluation admits, with the document data a window orders it by. */
type LocalMatch = { id: string; data: EntityStructure['data'] };

/** A remote entry or a local match competing for a window's slots, with its sort data read once. */
type WindowCandidate = { id: string; data: EntityStructure['data'] | undefined } & (
  | { entry: SourceEntry }
  | { source: SpaceQuerySource }
);

const isInProcess = (source: QuerySource): boolean => source.kind === 'local' || source.kind === 'registry';

/** Natural order: code units, as the host and the working-set executor sort ids. */
const compareIds = (left: string, right: string): number => (left < right ? -1 : left > right ? 1 : 0);

/**
 * Queries objects from the local working set.
 */
export class SpaceQuerySource implements QuerySource {
  public readonly changed = new Event<void>();

  readonly kind = 'local';

  private _ctx: Context = new Context();
  private _query: QueryAST.Query | undefined = undefined;
  private _results?: SourceEntry<Obj.Any>[] = undefined;
  readonly #covers = new WeakMap<QueryAST.Query, boolean>();
  /** Present when a write can be applied to cached results as a delta instead of a full recompute. */
  #delta?: { filter: QueryAST.Filter; options: QueryAST.QueryOptions | undefined } = undefined;
  private readonly _executor: WorkingSetQueryExecutor;
  private readonly _planner: QueryPlanner;

  constructor(private readonly _database: DatabaseImpl) {
    const provider: WorkingSetDataProvider = {
      get spaceId(): SpaceId {
        return _database.spaceId;
      },
      allCores: () => _database.allObjectCores(),
      getCoreById: (id, load) => _database.getObjectCoreById(id, { load: load ?? false }),
      areStrongDepsSatisfied: (core) => _database.areStrongDepsSatisfied(core),
      areStrongDepsResolved: (core) => _database.areStrongDepsResolved(core),
    };
    this._executor = new WorkingSetQueryExecutor(provider);
    this._planner = new QueryPlanner({ defaultTextSearchKind: 'full-text', noIndexes: true });
  }

  get spaceId() {
    return this._database.spaceId;
  }

  get spaceKey() {
    return this._database.spaceKey;
  }

  /** Objects each local commit in this database wrote, delivered before the commit's db update event. */
  get localCommits(): ReadOnlyEvent<ItemsUpdatedEvent> {
    return this._database._localCommitEvent;
  }

  isServing(): boolean {
    return this._query !== undefined;
  }

  answers(): number {
    return 0;
  }

  isFailed(): boolean {
    return false;
  }

  /** Whether `query` selects from this space, whether or not this source evaluates it itself. */
  coversQuery(query: QueryAST.Query): boolean {
    // Asked on every local write for every live query, and each answer walks the whole AST.
    let covers = this.#covers.get(query);
    if (covers === undefined) {
      covers = this._isValidSourceForQuery(query) && this._servesSpaceScope(query);
      this.#covers.set(query, covers);
    }
    return covers;
  }

  coreById(id: string): ObjectCore | undefined {
    return this._database.getObjectCoreById(id, { load: false });
  }

  /** Whether the object is loaded here with a body, so local evaluation decides its membership. */
  holdsBody(id: string): boolean {
    return this.coreById(id)?.getObjectStructure() !== undefined;
  }

  /**
   * Every object with an unindexed local write that local evaluation admits under the plan's flat filter and `admit`
   * accepts by its document data, with that data.
   */
  matchLocal(plan: MergePlan, admit?: (data: EntityStructure['data']) => boolean): LocalMatch[] {
    const matches: LocalMatch[] = [];
    // Only an object with a local write the index has not seen can be missing from an index answer.
    for (const id of this._database.unindexedWrites()) {
      const core = this.coreById(id);
      const data = core?.getObjectStructure()?.data;
      // `admit` reads one sort key and runs first, so the full filter only sees objects inside the window.
      if (
        core &&
        data !== undefined &&
        (admit === undefined || admit(data)) &&
        evaluateCore(plan, core) === true &&
        // Every other working-set result waits for its strong dependencies, so an admitted one does too.
        this._database.areStrongDepsSatisfied(core)
      ) {
        matches.push({ id, data });
      }
    }
    return matches;
  }

  /** A working-set entry for a loaded object, as `matchLocal` admitted it. */
  entryFor(id: string): SourceEntry<Obj.Unknown> | undefined {
    const result = this._database.getObjectById<Obj.Unknown>(id, { deleted: true });
    return result ? { id, result, match: { rank: 1 }, resolution: { source: 'local', time: 0 } } : undefined;
  }

  open(): void {}

  close() {
    this._results = undefined;
    void this._ctx.dispose().catch(() => {});
  }

  private _onUpdate = (updateEvent?: ItemsUpdatedEvent) => {
    if (!this._query) {
      return;
    }

    if (!updateEvent) {
      this._results = undefined;
      this.changed.emit();
      return;
    }

    if (this._results && this.#delta) {
      if (this.#applyDelta(this._results, this.#delta, updateEvent)) {
        this.changed.emit();
      }
      return;
    }

    // TODO(dmaretskyi): Could be optimized to recompute changed only to the relevant space.
    // Uncomputed results still signal a relevant write: a subscriber that has not read yet is notified of it
    // before the write returns, rather than at whatever later event first computes its results.
    const resultIds = new Set((this._results ?? []).map((result) => result.id));
    const changed = updateEvent.itemsUpdated.some(({ id: objectId }) => {
      // If any updated object was in previous results, invalidate.
      if (resultIds.has(objectId)) {
        return true;
      }

      // For simple queries, use the lightweight filter check for the newly-updated object.
      const trivial = isSimpleSelectionQuery(this._query!);
      if (trivial && !trivial.hasQueues) {
        const core = this._database._entityManager.getObjectCoreById(objectId, { load: false });
        return core != null && this._filterCore(core, trivial.filter, trivial.options);
      }

      // For complex queries (handled by executor), conservatively invalidate on any update.
      // The next getResults() call will recompute via the executor.
      return true;
    });

    if (changed) {
      this._results = undefined;
      this.changed.emit();
    }
  };

  async run(_ctx: Context, query: QueryAST.Query): Promise<SourceEntry<Obj.Unknown>[]> {
    if (!this._isValidSourceForQuery(query) || !this._servesSpaceScope(query) || queryHasWindowing(query)) {
      return [];
    }

    // Actively load by-id targets into the working set before executing. The executor's synchronous
    // getCoreById only kicks off a load, so a one-shot query would otherwise miss objects whose
    // (possibly linked) Automerge document has not been resolved into the working set yet.
    await this._preloadQueryIds(query);

    const items = this._executeWithWorkingSet(query);
    return items === null ? [] : this._mapItemsToResults(items);
  }

  /**
   * Loads any by-id selection targets into the working set. Preserves load-on-demand for
   * `Filter.id(...)` against linked (not-yet-resolved) Automerge documents, which the synchronous
   * executor cannot await. Feed-scoped by-id lookups are served by the index source, not the working set.
   */
  private async _preloadQueryIds(query: QueryAST.Query): Promise<void> {
    const ids = getSelectedIds(query);
    if (ids !== undefined) {
      await this._database.batchLoadObjectCores([...ids]);
    }
  }

  isSynchronous(): boolean {
    // The working set serves space-scoped selections synchronously. Feed-only queries, queries
    // with order/skip/limit clauses (see `queryHasWindowing`) and aggregates the executor declines
    // contribute nothing here, so they are not synchronous from this source's perspective.
    return (
      this._query !== undefined &&
      this._servesSpaceScope(this._query) &&
      !queryHasWindowing(this._query) &&
      !queryAggregateNeedsIndex(this._query)
    );
  }

  /** The working set is scanned on read, so this source never has an answer outstanding. */
  isPending(): boolean {
    return false;
  }

  getResults(): SourceEntry<Obj.Unknown>[] {
    if (!this._query) {
      return [];
    }

    if (!this._results) {
      this._results = this._computeResults(this._query!);
    }

    return this._results!;
  }

  private _computeResults(query: QueryAST.Query): SourceEntry<Obj.Unknown>[] {
    const items = this._executeWithWorkingSet(query);
    return items === null ? [] : this._mapItemsToResults(items);
  }

  update(query: QueryAST.Query): void {
    if (!this._isValidSourceForQuery(query) || !this._servesSpaceScope(query) || queryHasWindowing(query)) {
      this._query = undefined;
      return;
    }

    void this._ctx.dispose().catch(() => {});
    this._ctx = new Context();
    this._query = query;
    this.#delta = deltaFor(query);

    this._database._updateEvent.on(this._ctx, this._onUpdate);

    this._results = undefined;
    this.changed.emit();
  }

  /**
   * Applies written objects to the cached results, in the natural (id) order a full recompute produces, so a
   * synchronous notification per write costs the write rather than a working-set scan.
   *
   * @returns whether membership changed.
   */
  #applyDelta(
    results: SourceEntry<Obj.Any>[],
    { filter, options }: { filter: QueryAST.Filter; options: QueryAST.QueryOptions | undefined },
    event: ItemsUpdatedEvent,
  ): boolean {
    const entityManager = this._database._entityManager;
    let next = results;
    for (const { id } of event.itemsUpdated) {
      const core = entityManager.getObjectCoreById(id, { load: false });
      const matches = core != null && this._filterCore(core, filter, options);
      const index = next.findIndex((entry) => entry.id === id);
      if (index >= 0 && !matches) {
        next = next.toSpliced(index, 1);
      } else if (index < 0 && matches) {
        const position = next.findIndex((entry) => compareIds(entry.id, id) > 0);
        next = next.toSpliced(position < 0 ? next.length : position, 0, {
          id,
          result: this._database.getObjectById<Obj.Any>(id, { deleted: true }),
          match: { rank: 1 },
          resolution: { source: 'local', time: 0 },
        });
      }
    }
    if (next === results) {
      return false;
    }
    this._results = next;
    return true;
  }

  /**
   * Executes a query against the in-memory working set. Every query reaching this point (i.e. not
   * already excluded by `run`/`update` for windowing or feed scope) goes through the same planner
   * and executor -- there is no further fast path or query-shape gate. Returns null when the plan
   * requires SQL index capabilities the working set cannot satisfy (full-text or timestamp
   * selectors); the index-backed source handles those. A planner failure is a hard error: the
   * planner must accept any query the source is valid for, so a throw signals a bug rather than an
   * unsupported shape.
   */
  private _executeWithWorkingSet(query: QueryAST.Query): WorkingSetItem[] | null {
    const plan = this._planner.createPlan(query);
    return this._executor.tryExecute(plan);
  }

  /**
   * Maps working-set executor results, computing per-group counts once over the full set
   * (rather than per-item) so grouped queries report accurate counts.
   */
  private _mapItemsToResults(items: WorkingSetItem[]): SourceEntry<Obj.Unknown>[] {
    const groupCounts = new Map<string, number>();
    for (const item of items) {
      if (item.groupKey === undefined) {
        continue;
      }
      const serialized = GroupBy.serializeGroupKey(item.groupKey);
      groupCounts.set(serialized, (groupCounts.get(serialized) ?? 0) + 1);
    }
    return items.map((item) => this._mapItemToResult(item, groupCounts));
  }

  private _mapItemToResult(item: WorkingSetItem, groupCounts?: Map<string, number>): SourceEntry<Obj.Unknown> {
    const serializedGroupKey = item.groupKey !== undefined ? GroupBy.serializeGroupKey(item.groupKey) : undefined;
    return {
      id: item.objectId,
      result: this._database.getObjectById<Obj.Unknown>(item.objectId, { deleted: true }),
      match: { rank: 1 },
      resolution: {
        source: 'local',
        time: 0,
      },
      group:
        item.groupKey !== undefined
          ? { key: item.groupKey, count: groupCounts?.get(serializedGroupKey!) ?? 1 }
          : undefined,
    };
  }

  private _isValidSourceForQuery(query: QueryAST.Query): boolean {
    if (queryContainsChanges(query)) {
      return false;
    }

    const targetSpaces = getTargetSpacesForQuery(query);
    // Disabled by spaces filter.
    if (targetSpaces.length > 0 && !targetSpaces.includes(this.spaceId)) {
      return false;
    }

    // Disabled if the from clause has explicit scopes but none target spaces or feeds (e.g. registry-only).
    if (!queryTargetsSpacesOrFeeds(query)) {
      return false;
    }

    return true;
  }

  /**
   * Whether the query selects from the space working set: either it has no explicit `from` scope
   * (defaults to the owning space) or its `from` scope includes a space scope. Feed-only queries
   * return false — the working set holds no feed items.
   */
  private _servesSpaceScope(query: QueryAST.Query): boolean {
    let hasExplicitNonEmptyScope = false;
    let hasSpaceScope = false;
    QueryAST.visit(query, (node) => {
      if (node.type === 'from' && node.from._tag === 'scope' && node.from.scopes.length > 0) {
        hasExplicitNonEmptyScope = true;
        if (node.from.scopes.some((s) => s._tag === 'space')) {
          hasSpaceScope = true;
        }
      }
    });
    return !hasExplicitNonEmptyScope || hasSpaceScope;
  }

  private _filterCore(core: ObjectCore, filter: QueryAST.Filter, options: QueryAST.QueryOptions | undefined): boolean {
    // A core whose body has not landed matches nothing — there is no document to filter against.
    const structure = core.getObjectStructure();
    if (structure === undefined) {
      return false;
    }
    return (
      this._database.areStrongDepsSatisfied(core) &&
      filterCoreByDeletedFlag(core, options) &&
      filterMatchDoc(filter, {
        id: core.id,
        doc: structure,
        spaceId: this.spaceId,
      })
    );
  }
}

/**
 * The filter and options a write can be applied to as a delta: a single flat selection by type or wildcard
 * with predicates a single document decides, which is exactly what `_filterCore` evaluates. By-id selection
 * is excluded because the executor admits it on resolved rather than satisfied strong dependencies.
 */
const deltaFor = (
  query: QueryAST.Query,
): { filter: QueryAST.Filter; options: QueryAST.QueryOptions | undefined } | undefined => {
  const simple = isSimpleSelectionQuery(query);
  const { branches } = QueryAST.characteristics(query);
  const [branch] = branches;
  const eligible =
    simple !== null &&
    !simple.hasQueues &&
    branches.length === 1 &&
    branch.class === 'local-partial' &&
    (branch.selection === 'type' || branch.selection === 'wildcard') &&
    branch.predicates === 'local' &&
    branch.traversal === 0 &&
    branch.aggregate === 'none';
  return eligible ? { filter: simple.filter, options: simple.options } : undefined;
};

const filterCoreByDeletedFlag = (core: ObjectCore, options: QueryAST.QueryOptions | undefined): boolean => {
  switch (options?.deleted) {
    case undefined:
    case 'exclude':
      return !core.isDeleted();
    case 'include':
      return true;
    case 'only':
      return core.isDeleted();
  }
};

const getSelectedIds = (query: QueryAST.Query) => {
  const simple = isSimpleSelectionQuery(query);
  return simple?.filter.type === 'object' && simple.filter.id?.length ? simple.filter.id : undefined;
};

// Walks the shape `getSelectedIds` accepts, so a nested `from` query keeps its own filter.
const selectIds = (query: QueryAST.Query, ids: readonly EntityId[]): QueryAST.Query => {
  switch (query.type) {
    case 'options':
    case 'from':
      return { ...query, query: selectIds(query.query, ids) };
    case 'select':
      return query.filter.type === 'object' ? { ...query, filter: { ...query.filter, id: ids } } : query;
    default:
      return query;
  }
};
